import { useMemo } from 'react';
import { useAvContext } from '../context/AvContextProvider';
import { sortByStringField } from '../utils/utils';

/**
 * Returns a combined object of measurements and calculated dimensions from the AvContext.
 * The keys are the systemNames and values are the descriptor objects.
 */
export const useMetricsContextAsObject = () => {
  const { measurements, calculatedDimensions } = useAvContext();

  return useMemo(
    () => ({
      ...measurements.asObject,
      ...calculatedDimensions.asObject,
    }),
    [measurements.asObject, calculatedDimensions.asObject]
  );
};

/**
 * Returns a combined array of visible measurements and calculated dimensions from the AvContext.
 * Items are sorted by displayName.
 */
export const useMetricsContextAsArray = () => {
  const { measurements, calculatedDimensions } = useAvContext();

  return useMemo(
    () => sortByStringField([...measurements.visible, ...calculatedDimensions.visible], 'displayName'),
    [measurements.visible, calculatedDimensions.visible]
  );
};

/**
 * Returns a function that gets a metric by its system name.
 */
export const useGetMetric = () => {
  const metricsAsObject = useMetricsContextAsObject();

  return useMemo(
    () => ({
      getMetric: (name: string) => metricsAsObject[name],
    }),
    [metricsAsObject]
  );
};
